import { onButtonKeyDown } from "@storytell/ui/buttons";
import { linkifyText } from "@storytell/ui/util";
import { TbCheck, TbChevronRight, TbCopy } from "solid-icons/tb";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { GradientBorder, useCopy } from "../ui";

const COLLAPSIBLE_LINES = 6;
const COLLAPSIBLE_CHARS = 600;
const linkClass =
  "text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline underline-offset-2";

interface PromptEchoCardProps {
  text: string;
  /** Author label; defaults to the host's "You". */
  author?: string;
  /**
   * Render the text as source rather than prose. For a prompt that is an
   * assembled payload — markdown headings around a YAML block — the markup is
   * the content, and proportional type sets it as if the `##` and `---` were
   * mistakes.
   */
  source?: boolean;
  /** Whether a collapsible prompt starts open. Defaults to open. */
  defaultExpanded?: boolean;
}

/**
 * A prompt Chief assembled and sent on the user's behalf (an agent's opening
 * bundle). What the user typed renders in PromptCard.
 */
export function PromptEchoCard(props: PromptEchoCardProps) {
  const rt = useChatRuntime();
  const { copied, copy } = useCopy({ timeout: 1000, write: rt.actions.copyText });

  const lineCount = createMemo(() => props.text.split("\n").length);
  const isCollapsible = () => lineCount() > COLLAPSIBLE_LINES || props.text.length > COLLAPSIBLE_CHARS;
  const [isExpanded, setIsExpanded] = createSignal(props.defaultExpanded ?? true);

  const previewText = createMemo(() => {
    const firstLine = props.text.split("\n")[0] ?? "";
    return firstLine.slice(0, 80).trim();
  });

  return (
    <GradientBorder rounded="rounded-2xl" bgColor="bg-panel" expandOnHover>
      <div
        data-renderer="prompt-echo-card"
        class="group flex w-full items-start gap-4 bg-panel px-5 py-3.5 hover:bg-panel-hover"
      >
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-2">
            <div
              role="button"
              tabIndex={isCollapsible() ? 0 : -1}
              aria-disabled={!isCollapsible()}
              aria-expanded={isCollapsible() ? isExpanded() : undefined}
              class="flex min-w-0 flex-1 items-center gap-2"
              classList={{ "cursor-pointer": isCollapsible() }}
              onClick={() => {
                if (isCollapsible()) setIsExpanded((v) => !v);
              }}
              onKeyDown={onButtonKeyDown(() => {
                if (isCollapsible()) setIsExpanded((v) => !v);
              })}
            >
              <Show when={isCollapsible()}>
                <TbChevronRight
                  size={14}
                  class="shrink-0 stroke-3 text-muted-text transition-transform duration-100"
                  classList={{ "rotate-90": isExpanded() }}
                />
              </Show>
              <span class="shrink-0 text-sm text-secondary-text">{props.author ?? rt.t("common.you")}</span>
              <Show when={isCollapsible() && !isExpanded()}>
                <span class="max-w-75 truncate text-sm text-muted-text">{previewText()}...</span>
                <span class="shrink-0 text-xs text-muted-text">
                  ({lineCount()} {rt.t("prompt_lines_count")})
                </span>
              </Show>
            </div>

            <div
              class="flex items-center gap-2 text-xs opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              classList={{ hidden: !props.text }}
            >
              <button
                type="button"
                title={copied() ? rt.t("common.copied") : rt.t("common.copy_prompt_to_clipboard")}
                class="group/btn cursor-pointer rounded-lg border border-muted-border bg-tile p-1 shadow-md hover:bg-tile-hover"
                onClick={() => void copy(props.text)}
              >
                <Dynamic
                  component={copied() ? TbCheck : TbCopy}
                  size={16}
                  class="stroke-2 transition-transform group-hover/btn:scale-hover group-active/btn:scale-active"
                />
              </button>
            </div>
          </div>

          <Show when={props.text && (!isCollapsible() || isExpanded())}>
            <div
              class="mt-2 wrap-break-word whitespace-pre-wrap text-primary-text"
              classList={{
                "text-sm leading-relaxed": !props.source,
                "max-h-80 overflow-y-auto rounded-md bg-canvas p-3 font-mono text-[0.6875rem] leading-relaxed text-secondary-text":
                  props.source,
              }}
            >
              <Show when={!props.source} fallback={props.text}>
                <For each={linkifyText(props.text)}>
                  {(segment) =>
                    segment.type === "link" ? (
                      <a
                        href={segment.value}
                        draggable={false}
                        target="_blank"
                        rel="noopener noreferrer"
                        class={linkClass}
                        onClick={(e) => {
                          if (rt.actions.openExternalUrl) {
                            e.preventDefault();
                            rt.actions.openExternalUrl(segment.value);
                          }
                        }}
                      >
                        {segment.value}
                      </a>
                    ) : (
                      segment.value
                    )
                  }
                </For>
              </Show>
            </div>
          </Show>
        </div>
      </div>
    </GradientBorder>
  );
}
